import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { global } from "@shared/libs/Global";
import { getInitPopUpData } from "@shared/utils/CommonUtils";
import SnForm from "@widgets/SnForm";
import { callApi } from "@shared/api/restApi";
import { showDialog } from "@widgets/CommonDialog";

/**
 * SN_SFA_PJT_REG_002_POP_001 — 프로젝트 등록/수정 팝업 (ISS2026100053)
 * 폼 컨트롤은 sys_popupctrl(cd_form='freeform1') 메타 구동 → SnForm 패널.
 *   - 프로젝트코드/명 · 프로젝트구분(PMS001) · WBS템플릿(pms_wbsh) · 고객사(POPUP_CUST) · PM(POPUP_EMP) · 계획시작/종료
 *   - 등록: pms_pjth insert + 선택 템플릿 복사(SN_SFA_PJT_REG_002_PJT_SAVE_01), 모달 "등록"(checkValidity)
 *   - 수정(editPjt 지정, 간트 프로젝트 행 더블클릭): 코드 읽기전용, 템플릿 자리에 사용여부.
 *     사용여부 해제 후 저장 = 삭제(사용안함, yn_use='N'). 메타는 그대로 두고 수정 모드에서만 컨트롤 정의를 덮어쓴다.
 *   - 날짜(SnDateBox) YYYYMMDD 방출 → 변환 없이 전달
 */
interface Props {
  editPjt?: string; // 있으면 수정 모드
}

// 수정 모드 컨트롤 덮어쓰기: 코드 읽기전용, 템플릿 → 사용여부, 고객사·PM 명칭 표시
const toEditControls = (controls: any[]) =>
  controls.map((c) => {
    if (c.cd_param === "cd_pjt") return { ...c, dc_editcon: "true" };
    if (c.cd_param === "cd_template")
      return { ...c, tp_ctrl: "SnCheckBox", cd_param: "yn_use", nm_label: "사용여부", cd_displaycolumn: "사용", cd_clas: null, dc_sql: null, cd_value: "Y" };
    if (c.cd_param === "cd_cust") return { ...c, cd_displaycolumn: "nm_cust" };
    if (c.cd_param === "cd_emp") return { ...c, cd_displaycolumn: "nm_emp" };
    return c;
  });

const SN_SFA_PJT_REG_002_POP_001 = forwardRef(({ editPjt }: Props, ref: React.Ref<any>) => {
  const { userInfo } = global();
  const freeFormRef = useRef<any>(null);
  const [formData, setFormData] = useState<any>();
  const [freeform1Controls, setFreeform1Controls] = useState<any[]>([]);
  const origYnUse = useRef<string>("Y");

  useImperativeHandle(ref, () => ({
    checkValidity: async () => await onSave(),
  }));

  useEffect(() => {
    (async () => {
      try {
        const rtn: any = await getInitPopUpData("SN_SFA_PJT_REG_002_POP_001", {});
        const controls = rtn.form?.freeform1 || [];
        if (!editPjt) {
          setFreeform1Controls(controls);
          setFormData(rtn.formData);
          return;
        }
        const res = await callApi("SN_SFA_PJT_REG_002_PJT_GET_01", { cd_firm: userInfo.cd_firm, cd_pjt: editPjt });
        const row = Array.isArray(res?.data) ? res.data[0] : undefined;
        if (!row) {
          await showDialog("alter", "프로젝트 정보를 찾을 수 없습니다.");
          return;
        }
        origYnUse.current = row.yn_use || "Y";
        setFreeform1Controls(toEditControls(controls));
        setFormData({ ...rtn.formData, ...row });
      } catch (e) {
        /* restApi 처리 */
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editPjt]);

  const onSave = async (): Promise<boolean> => {
    const f = freeFormRef.current?.getFormdata() || {};
    if (!String(f.cd_pjt || "").trim()) { await showDialog("alter", "프로젝트코드를 입력하세요."); return false; }
    if (!String(f.nm_pjt || "").trim()) { await showDialog("alter", "프로젝트명을 입력하세요."); return false; }
    if (!f.dt_start || !f.dt_end) { await showDialog("alter", "계획 시작/종료일을 입력하세요."); return false; }
    if (String(f.dt_end) < String(f.dt_start)) { await showDialog("alter", "계획 종료일이 시작일보다 빠릅니다."); return false; }

    try {
      if (editPjt) {
        const ynUse = f.yn_use === "N" ? "N" : "Y";
        if (ynUse === "N" && origYnUse.current !== "N") {
          const ok = await showDialog(
            "confirm",
            "사용여부를 해제하면 프로젝트가 삭제(사용안함) 처리됩니다.\n진행하시겠습니까?"
          );
          if (!ok) return false;
        }
        const res = await callApi("SN_SFA_PJT_REG_002_PJT_UPDATE_01", {
          cd_firm: userInfo.cd_firm,
          cd_pjt: editPjt, // 코드는 바꾸지 않는다
          nm_pjt: String(f.nm_pjt).trim(),
          fg_pjt: f.fg_pjt || "1",
          cd_cust: f.cd_cust || "",
          cd_emp: f.cd_emp || "",
          dt_start: f.dt_start,
          dt_end: f.dt_end,
          yn_use: ynUse,
        });
        return !!res?.status;
      }

      const res = await callApi("SN_SFA_PJT_REG_002_PJT_SAVE_01", {
        cd_firm: userInfo.cd_firm,
        cd_biz: userInfo.cd_biz,
        cd_pjt: String(f.cd_pjt).trim(),
        nm_pjt: String(f.nm_pjt).trim(),
        fg_pjt: f.fg_pjt || "1",
        cd_cust: f.cd_cust || "",
        cd_emp: f.cd_emp || "",
        dt_start: f.dt_start,
        dt_end: f.dt_end,
        cd_template: f.cd_template || "",
      });
      // callApi 는 항상 { status, data } 객체를 돌려주므로 status 로 성공 판단
      return !!res?.status;
    } catch (e) {
      return false;
    }
  };

  return (
    <div className="h-full flex flex-col px-2 pb-2">
      {/* 부제목 (시안) */}
      <div className="px-1 pb-3 mb-3 text-xs text-[#64748b] border-b border-[#e5e7eb] shrink-0">
        {editPjt
          ? "프로젝트 기본정보를 수정합니다. 프로젝트코드와 WBS 템플릿은 바꿀 수 없습니다."
          : "프로젝트 생성 후 WBS를 검토하고 계획을 확정합니다."}
      </div>
      <div className="flex-1 min-h-0">
        <SnForm ref={freeFormRef} data={formData} controls={freeform1Controls} popupRef={ref} />
      </div>
      {/* 안내 박스 (시안) */}
      <div className="mt-3 shrink-0 rounded-md border border-[#e2e8f0] bg-[#f8fafc] px-4 py-3 text-xs leading-5 text-[#475569]">
        {editPjt ? (
          <>
            사용여부를 해제하고 저장하면 프로젝트가 삭제(사용안함) 처리되어 목록에서 숨겨집니다.
            <br />
            조회조건의 사용여부 체크를 풀면 다시 보이며, 사용여부를 체크해 저장하면 복원됩니다.
          </>
        ) : (
          <>
            WBS 템플릿을 선택하면 템플릿의 ACTIVITY와 가중치가 복사되고, 계획일자는 계획시작일·계획종료일로 채워집니다.
            <br />
            ACTIVITY별 계획일자 조정과 실적 입력은 WBS 진행등록에서 합니다.
          </>
        )}
      </div>
    </div>
  );
});

export default SN_SFA_PJT_REG_002_POP_001;
